import React from 'react';
import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid';
import { Plugin } from 'prosemirror-state';

import { EditorOptions } from '@lyearn/lygo-core';

import { removeMenuContainer, run, setPluginMeta } from '../../utils';
import {
  EMOJI_CONTAINER_ID,
  EMOJI_CONTAINER_SELECTOR,
  EMOJI_OPEN_REGEX,
  EMOJI_PLUGIN_KEY,
  EVENT_KEYS,
} from './consts';
import EmojiPopover from './EmojiPopover';
import { EmojiPluginProps, EmojiPluginState } from './types';

const getEmojiPlugin = (itemProps: EmojiPluginProps) => {
  return ({ portalApi }: EditorOptions) => {
    return [
      new Plugin({
        key: EMOJI_PLUGIN_KEY,
        state: {
          init() {
            return {
              open: false,
            };
          },
          apply(tr, old) {
            const props = tr.getMeta(EMOJI_PLUGIN_KEY);
            if (props) return props;
            return old;
          },
        },
        view: (editorView) => {
          const portalKey = nanoid();

          // This will be called only once. It will add a container div.
          // Emoji Menu Button will be rendered in this container div.
          const emojiPopoverContainer = document.createElement('div');
          emojiPopoverContainer.id = EMOJI_CONTAINER_ID;
          editorView.dom?.parentNode?.appendChild(emojiPopoverContainer);

          return {
            update: (view) => {
              const props = EMOJI_PLUGIN_KEY.getState(view.state);
              const dom = view.dom?.parentNode?.querySelector(EMOJI_CONTAINER_SELECTOR);

              if (dom) {
                portalApi.createPortal(
                  ReactDOM.createPortal(
                    <EmojiPopover {...props} ListComponent={itemProps.ListComponent} />,
                    dom,
                    portalKey,
                  ),
                );
              }
            },
            destroy: () => {
              portalApi.destroyPortal(portalKey);
              removeMenuContainer(EMOJI_CONTAINER_ID, editorView);
            },
          };
        },

        props: {
          handleDOMEvents: {
            mousedown: (editorView) => {
              const state: EmojiPluginState = {
                open: false,
                editorView,
                match: '',
              };
              setPluginMeta(editorView, EMOJI_PLUGIN_KEY, state);
              return false;
            },
            keydown: (editorView, event) => {
              const prevProps = EMOJI_PLUGIN_KEY.getState(editorView.state);
              // Ignore this keypresses here. It will be handled in the EmojiPopover.
              if (prevProps.open === true && EVENT_KEYS.includes(event.key)) {
                return true;
              }
              // timeout ensures that the add/delete has been handled by prosemirror
              // and any characters removed, before we evaluate the rule.
              setTimeout(() => {
                const { pos } = editorView.state.selection.$from;
                return run(editorView, pos, pos, EMOJI_OPEN_REGEX, (_state: any, match: any) => {
                  const state: EmojiPluginState = { open: false, editorView, match: '' };

                  try {
                    if (match) {
                      state.open = true;
                      state.match = match[1];
                    } else {
                      state.open = false;
                    }
                    setPluginMeta(editorView, EMOJI_PLUGIN_KEY, state);
                  } catch (error) {
                    console.log({ error });
                  }

                  return null;
                });
              });

              return false;
            },
          },
        },
      }),
    ];
  };
};

export { EMOJI_PLUGIN_KEY };
export default getEmojiPlugin;
